import { fuego } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { PetAction, UserPet } from '@typings/pets/pet.interface';
import { updateUserWallet } from '@store/firebase/user/updateUser';
import replaceInteractionPlaceholders, {
  genWeightedTemplate as fetchWeightedTemplate,
} from './generateLogs';
import { pickOpponentPetCandidate } from './pickOpponentPet';
import { WalletHistoryType } from '@store/firebase/user/addWalletHistoryByType';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

type ExecuteParams = {
  action: PetAction;
  activePet?: UserPet;
  currentUid?: string | null;
  maxHunger?: number;
  computeLevelFromExp?: (exp: number) => number;
  petDocPath?: string;
  setActivePetDoc?: (data: Partial<UserPet>, opts?: any) => Promise<void>;
  setUserPets?: (updater: (prev: UserPet[]) => UserPet[]) => void;
  setLevelUpInfo?: (info: { from: number; to: number }) => void;
  setActionCooldowns?: (updater: (prev: any[]) => any[]) => void;
};

// 액션 효과(경험치/배고픔/재화/상호작용/액션로그)를 일괄 적용
export async function applyActionEffects({
  action,
  activePet,
  currentUid,
  maxHunger,
  computeLevelFromExp,
  petDocPath,
  setActivePetDoc,
  setUserPets,
  setLevelUpInfo,
  setActionCooldowns,
}: ExecuteParams) {
  if (!activePet || !currentUid) return;

  // 1) 경험치/배고픔 적용 및 저장 (로컬 + 원격)
  try {
    const petRef = fuego.db.doc(
      `${CollectionPath.USERS}/${currentUid}/pets/${activePet.id}`,
    );
    
    const expDelta = Number(action.effects?.petExp) || 0;
    const hungerDelta = Number(action.effects?.hungerDecrease) || 0;
    
    // Firestore increment 사용하여 원자적 업데이트 (동시 클릭 문제 해결)
    const incrementPatch: any = {
      updatedAt: new Date(),
    };
    
    // 로컬 상태용 패치 (UI 즉시 반영)
    const localPatch: any = {};
    
    if (expDelta !== 0) {
      incrementPatch.experience = firebase.firestore.FieldValue.increment(expDelta);
      const newLocalExp = (Number(activePet.experience) || 0) + expDelta;
      localPatch.experience = newLocalExp;
      console.log(`✅ 액션 경험치 업데이트: +${expDelta} (예상: ${newLocalExp})`);
    }
    
    if (hungerDelta !== 0) {
      // 배고픔은 감소하므로 음수로 increment
      const currentHunger = Number(activePet.hunger) || 0;
      const newHunger = Math.max(0, currentHunger - hungerDelta);
      // 배고픔은 0 이하로 내려가지 않아야 하므로 직접 값 설정
      incrementPatch.hunger = newHunger;
      localPatch.hunger = newHunger;
    }
    
    const hasChanges = expDelta !== 0 || hungerDelta !== 0;
    
    if (hasChanges) {
      // 원격 저장 먼저 (increment로 원자적 업데이트)
      try {
        await petRef.set(incrementPatch, { merge: true } as any);
        console.log('✅ Firestore 펫 상태 저장 완료');
      } catch (e) {
        console.warn('펫 상태 저장 실패', e);
      }
      
      // 로컬 반영 (UI 즉시 업데이트)
      if (setUserPets && Object.keys(localPatch).length > 0) {
        setUserPets((prev) =>
          prev.map((p) => (p.id === activePet.id ? { ...p, ...localPatch } : p)),
        );
      }
      
      // 레벨 계산 및 표시
      if (
        typeof computeLevelFromExp === 'function' &&
        typeof localPatch.experience === 'number'
      ) {
        const prevLevel = Number(activePet.level) || 1;
        const nextLevel = computeLevelFromExp(localPatch.experience);
        if (nextLevel > prevLevel) {
          if (setLevelUpInfo)
            setLevelUpInfo({ from: prevLevel, to: nextLevel });
          if (setUserPets) {
            setUserPets((prev) =>
              prev.map((p) =>
                p.id === activePet.id ? { ...p, level: nextLevel } : p,
              ),
            );
          }
          // 레벨도 Firestore에 저장
          try {
            await petRef.set({ level: nextLevel }, { merge: true } as any);
          } catch (e) {
            console.warn('레벨 저장 실패', e);
          }
        }
      }
    }
  } catch (e) {
    console.warn('펫 상태 업데이트 실패', e);
  }

  // 2) 재화 지급
  try {
    if (action.effects?.currency?.amount) {
      await updateUserWallet({
        uids: [currentUid],
        price: Number(action.effects.currency.amount),
        type: WalletHistoryType.PETACTION,
        memo: activePet.customName || '나의 펫',
      });
    }
  } catch (e) {
    console.warn('재화 지급 실패', e);
  }

  // 2.5) 액션 로그 저장 (+ 트리거 태그)
  try {
    const expGain = Number(action.effects?.petExp || 0);
    const hungerDec = Number(action.effects?.hungerDecrease || 0);
    const currencyAmount = Number(action.effects?.currency?.amount || 0);
    const willTriggerInteraction = Boolean(
      action.effects?.triggerInteraction || action.effects?.petMaxHunger,
    );

    const tags: string[] = [];
    if (expGain > 0) tags.push(`경험치:+${expGain}`);
    if (hungerDec > 0) tags.push(`배고픔:-${hungerDec}`);
    if (currencyAmount > 0) tags.push(`재화:+${currencyAmount}`);
    if (willTriggerInteraction) tags.push('상호작용 문구');
    const tagStr = tags.length ? ` [${tags.join(' | ')}]` : '';

    const actionMessage = `${activePet.customName}이(가) ${action.name}을(를) 사용했습니다. ${tagStr}`;
    const colPath = `${CollectionPath.USERS}/${currentUid}/pets/${activePet.id}/logs`;
    await fuego.db.collection(colPath).add({
      id: `act_${Date.now()}`,
      userId: currentUid,
      petId: activePet.id,
      type: 'action',
      message: actionMessage,
      createdAt: new Date(),
      tags: {
        exp: expGain,
        hungerDecrease: hungerDec,
        currency: currencyAmount,
        triggerInteraction: willTriggerInteraction,
      },
    } as any);
  } catch (_e) {
    // 무시 (표시 목적 로그 실패)
  }

  // 3) 상호작용 트리거
  try {
    if (action.effects?.triggerInteraction || action.effects?.petMaxHunger) {
      const template = await fetchWeightedTemplate();
      if (template) {
        const opp = await pickOpponentPetCandidate({
          excludeUserId: currentUid || null,
        } as any);
        const msg = replaceInteractionPlaceholders(template, {
          myPetName: activePet.customName || '나의펫',
          opponentPetName: (opp as any)?.customName || '상대펫',
        });
        const colPath = `${CollectionPath.USERS}/${currentUid}/pets/${activePet.id}/logs`;
        await fuego.db.collection(colPath).add({
          id: `rand_${Date.now()}`,
          userId: currentUid,
          petId: activePet.id,
          type: 'random_dialogue',
          message: msg,
          createdAt: new Date(),
          isAutoGenerated: true,
        } as any);
      }
    }
  } catch (e) {
    console.warn('상호작용 생성 실패', e);
  }

  // 4) 쿨다운 업데이트 (옵션)
  try {
    if (setActionCooldowns) {
      setActionCooldowns((prev: any[]) => {
        const updated = prev.filter((cd: any) => cd.actionId !== action.id);
        const nextCd = {
          actionId: action.id,
          lastUsed: new Date(),
          cooldownMinutes: action.cooldown,
        };
        updated.push(nextCd);
        try {
          const key = `pet_cooldowns_${currentUid || 'guest'}`;
          const map = Object.fromEntries(
            updated.map((c: any) => [
              c.actionId,
              { lastUsed: c.lastUsed, cooldownMinutes: c.cooldownMinutes },
            ]),
          );
          localStorage.setItem(key, JSON.stringify(map));
        } catch (_err) {}
        return updated;
      });
    }
  } catch (_e) {}
}

export default applyActionEffects;
